<template>
  <div>
    <Spinner v-if="loading" />
    
    <StaticDataTable class="static-table-list order-item-list" v-if="!loading" :striped="false" :hover="editing || showTotals">
      <template v-slot:header>
        <tr v-if="editing">
          <th width="5%">Type</th>
          <th width="18%">Stock</th>
          <th width="12%">Supplier Description</th>
          <th width="11%">Size (H x W)</th>
          <th width="6%">Container</th>
          <th width="5%">Block Size</th>
          <th width="8%">No. Blocks</th>
          <th width="5%">Total Units</th>
          <th width="6%">Unit Of Measure</th>
          <th width="8%">Price / Unit</th>
          <th width="6%">Tax</th>
          <th width="6%">Line Price</th>
          <th width="5%">Actions</th>
        </tr>
        <tr v-if="!editing">
          <th>Type</th>
          <th>Stock</th>
          <th>Supplier Description</th>
          <th>Size (HxW)</th>
          <th>Container</th>
          <th>Block Size</th>
          <th>Block Qty</th>
          <th>Total Units</th>
          <th>Price / Unit</th>
          <th>Tax Type</th>
          <th>Line Price</th>
        </tr>
      </template>
      <template v-slot:body>
        <template v-for="(item, index) in itemList">

          <tr v-if="index > 0" class="order-item-spacer" :key="`item-${index}-spacer`"><td colspan="12"> </td></tr>

          <tr class="order-item-row order-item-row-group-glass" :key="`item-${index}`">
            <template v-if="editing">
              <td class="order-item-type-details">
                <div class="font-weight-bold order-item-type">Glass</div>
                
                <div class="order-item-icon-list">
                  <OrderItemIcon
                    :label="item.priceError"
                    v-if="item.priceError"
                    :value="item.priceError != ''"
                    :editing="false"
                    icon="fa-usd-circle"
                    iconStyle="fas"
                    iconClass="order-item-icon-price-error"
                    iconStackClass="order-item-icon-price-error-bg" />

                  <OrderItemIcon
                    :label="`Total Item Weight: ${formatDecimal(calculateItemWeight(item), 0, true)}kg`"
                    :value="true"
                    :editing="false"
                    icon="fa-weight"
                    iconStyle="far"
                    iconClass="order-item-icon-note" />
                </div>
              </td>
              <td>
                <FormSearchableSelect :options="stockLookupList" :value="item.stockId" @input="setItemStockId(item, $event)" placeholder="Stock Code" />
              </td>
              <template v-if="!item.stockId || item.loading">
                <td colspan="10"> <Spinner v-if="item.loading" /></td>
              </template>
              <template v-if="item.stockId && !item.loading">
                <td>
                  <FormInputText v-model="item.description" @input="itemListUpdated()" placeholder="Supplier Alias" />
                </td>
                <td>
                  <button class="btn btn-sm btn-white" @click.prevent="selectGlassSize(item)" v-if="!item.glassSizeId"><i class="far fa-layer-plus"></i> Select Glass</button>
                  <a v-if="item.glassSizeId" href="#" class="order-item-editable-link" @click.prevent="selectGlassSize(item)">{{item.glassHeight}} x {{item.glassWidth}}</a>
                </td>
                <td>{{item.stockContainerCode}}</td>
                <td>{{item.blockSize}}</td>
                <td>
                  <FormInputText type="number" v-model="item.blockQuantity" @input="itemListUpdated()" />
                </td>
                <td>{{calculateItemTotalUnits(item) | formatDecimal(5)}}</td>            
                <td v-html="uomItemDisplayUnits(item)"></td>
                <td>
                  <FormInputText type="number" v-model="item.unitPrice" @input="itemListUpdated()" />
                </td>
                <td>
                  <FormSelect :options="taxOptions" v-model="item.taxRateId" @input="taxRateUpdated(item, $event); itemListUpdated()" />
                </td>
                <td>{{calculateItemLinePrice(item) | formatCurrency}}</td>
              </template>
              <td>
                <ActionDropMenu :tableAction="true" v-if="!item.loading">
                  <DropdownMenuItem @click="removeItem(index)" icon="fa-trash-alt">Remove Item</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </template>
            <template v-else>
              <td class="order-item-type-details">
                <div class="font-weight-bold order-item-type">{{item.stockType | formatLookupName(InventoryStockTypeLookup)}}</div>
                <div class="order-item-icon-list">
                  <OrderItemIcon
                    :label="item.priceError"
                    v-if="item.priceError"
                    :value="item.priceError != ''"
                    :editing="false"
                    icon="fa-usd-circle"
                    iconStyle="fas"
                    iconClass="order-item-icon-price-error"
                    iconStackClass="order-item-icon-price-error-bg" />

                  <OrderItemIcon
                    :label="`Total Item Weight: ${formatDecimal(calculateItemWeight(item), 0, true)}kg`"
                    :value="true"
                    :editing="false"
                    icon="fa-weight"
                    iconStyle="far"
                    iconClass="order-item-icon-note" />
                </div>
              </td>
              <td>
                <LinkStock v-if="item.stockId" :id="item.stock.id" :name="`${item.stock.code} - ${item.stock.name}`" />
              </td>
              <td>{{item.description}}</td>
              <td>{{item.glassHeight}} x {{item.glassWidth}}</td>
              <td>
                <template v-if="item.glassSizeId && item.glassSize.stockContainer">
                  {{item.glassSize.stockContainer.code}}
                </template>
              </td>
              <td>{{item.glassSize.blockSize}}</td>
              <td>{{item.blockQuantity}}</td>
              <td>{{calculateItemTotalUnits(item) | formatDecimal(5)}}</td>
              <td>{{item.unitPrice | formatCurrency}}</td>
              <td>{{item.taxRate ? item.taxRate.code : 'None'}}</td>
              <td class="font-weight-bold">{{(calculateItemTotalUnits(item) * item.unitPrice) | formatCurrency}}</td>
            </template>
          </tr>
        </template>
        <tr class="static-datatable-row-nostripe border-top" v-if="editing">
          <td colspan="2">
            <a class="btn btn-white btn-xs" @click.prevent="addItemClicked" v-if="editing"><i class="far fa-plus"></i> Add Item</a>
          </td>
          <td class="text-left">
            <strong>Total Weight:</strong> {{formatDecimal(calculateTotalWeight(), 0, true)}}kg
          </td>
          <td class="text-right font-weight-bold">
            Container: 
          </td>
          <td>
            <template v-if="containsMixedContainers()">
              <Tooltip label="Cannot mix container types">
                <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon> Mixed
              </Tooltip>
            </template>
            <template v-else>
              <span class="font-weight-bold">{{calculateContainerInfo()}}</span>
            </template>
          </td>
          <td> </td>
          <td>
            <template v-if="containerOverCapacity()">
              <Tooltip label="Container is over capacity">
                <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon> {{totalBlockQuantity()}} / {{totalContainerBlockCapacity()}}
              </Tooltip>
            </template>
            <template v-else>
              <span class="font-weight-bold">{{totalBlockQuantity()}} / {{totalContainerBlockCapacity()}}</span>
            </template>              
          </td>
          <template v-if="showTotals">
            <td colspan="2"> </td>
            <td colspan="2" class="text-right">Sub Total:</td>
            <td colspan="2">{{calculateSubTotalPrice() | formatCurrency}} {{currencyCode}}</td>
          </template>
          <td v-if="!showTotals" colspan="6"> </td>
        </tr>

        <template v-if="!editing && showTotals">
          <tr class="static-datatable-row-nostripe border-top table-totals-row">
            <td class="text-left" colspan="2">
              <strong>Total Weight:</strong> {{formatDecimal(calculateTotalWeight(), 0, true)}}kg
            </td>
            <td colspan="7"> </td>
            <td class="text-right">Sub Total:</td>
            <td colspan="2">{{calculateSubTotalPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row">
            <td colspan="9"> </td>
            <td class="text-right">{{taxName}}:</td>
            <td colspan="2">{{calculateTaxPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row table-totals-row-grand-total">
            <td colspan="9"> </td>
            <td class="text-right">Total:</td>
            <td colspan="2">{{currencyCode}} {{calculateTotalPrice() | formatCurrency}}</td>
          </tr>
        </template>
        <template v-if="editing && showTotals">
          <tr class="static-datatable-row-nostripe table-totals-row">
            <td colspan="9"> </td>
            <td colspan="2" class="text-right">{{taxName}}:</td>
            <td colspan="2">{{calculateTaxPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row table-totals-row-grand-total">
            <td colspan="9"> </td>
            <td colspan="2" class="text-right">Total:</td>
            <td colspan="2">{{currencyCode}} {{calculateTotalPrice() | formatCurrency}}</td>
          </tr>
        </template>
      </template>
    </StaticDataTable>
    
  </div>
</template>

<script>
import _ from 'lodash'
import currency from 'currency.js'

import Utils from '@/utils/utils'
import StaticDataTable from '@/components/table/staticDataTable.vue'
import OrderItemIcon from '@/modules/orders/components/orderItemIcon'
import OrderSelectGlassSizeDialog from '@/modules/orders/views/orderSelectGlassSizeDialog'

import { InventoryStockTypes, InventoryStockTypeLookup, UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'BulkOrderItemList',
  components: {
    StaticDataTable,
    OrderItemIcon
  },
  model: {
    prop: 'value',
    event: 'update'
  },
  props: {
    editing: Boolean,
    value: Array,
   contactId: [Number, String],
    showTotals: {
      type: Boolean,
      default: true
    },
    currencyCode: String
  },
  data() {
    return {
      loading: false,
      containerList: [],
      stockList: [],
      stockLookupList: [],
      taxRateList: [],
      taxName: 'Tax',
      taxOptions: [],
      supplierBulkPricingData: {},
      supplierDefaultTaxId: null,
      itemList: this.$props.value
    }
  },
  watch: {
    value: function(newVal, oldVal) {
      this.$data.itemList = _.cloneDeep(newVal)
    },
   contactId: function(newVal, oldVal) {
      this.requestSupplierPricingData()
    },
    editing: function(newVal, oldVal) {
      // If now editing: load the data, then apply some lookups to each item
      if(newVal) {
        this.loading = true

        this.requestData({ ignoreLoadingState: true })
        .then(async () => {

          // Load the per-item details for the dropdowns
          for(var item of this.itemList) {
            let originalItem = _.cloneDeep(item)

            // Store some details that get overridden by the events
            let unitPrice = item.unitPrice

            // Set stock & then request stock details
            await this.setItemStockId(item, item.stockId, false)        

            // Set glass size info
            this.glassSizeUpdated(item, item.glassSizeId, {
              isCustomStockLinkedGlassSize: originalItem.isCustomStockLinkedGlassSize,
              glassHeight: originalItem.glassHeight,
              glassWidth: originalItem.glassWidth
            })

            item.unitPrice = unitPrice
          }

          this.loading = false
        })
      }
    }
  },
  created() {
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
  },
  mounted() {
    // Note: Calling page of the entry list will call the requestData() method
    // It needs to know when loaded to perform other actions

    this.requestSupplierPricingData()
  },
  methods: {
    // Request associated data with the lookups for the editor
    // Options are:
    //   ignoreLoadingState: Ignores the loading state, useful when loading an existing list to stop UI popping effects while loading/setting values
    requestData(options = {}) {
      return new Promise((resolve, reject) => {
        if(!options.ignoreLoadingState)
          this.loading = true

        Promise.all([
          this.$apiService.inventoryService.getStockList(),
          this.$apiService.inventoryService.getStockContainerList(),
          this.$apiService.adminService.getTaxRateList()
        ])
        .then(results => {
          this.$data.stockList = results[0].data
          this.$data.containerList = results[1].data
          this.$data.taxRateList = results[2].data

          this.$data.stockLookupList = this.$data.stockList
            .filter(stock => stock.isActive && stock.type == InventoryStockTypes.glass)
            .map(stock => {
              return {
                value: stock.id,
                name: `${stock.code} | ${stock.name}`
              }
            })

          this.$data.taxOptions = this.$data.taxRateList.map(taxRate => {
            return { name: taxRate.code, value: taxRate.id }
          })
          this.$data.taxOptions.unshift({ name: 'None', value: null })
        })
        .finally(() => {
          if(!options.ignoreLoadingState)
            this.loading = false

          return resolve()
        })
      })
    },
    requestSupplierPricingData() {
      this.supplierBulkPricingData = {}
      if(!this.$props.contactId)
        return

      // Get default tax info for supplier
      this.$apiService.contactService.getContact(this.$props.contactId)
      .then(response => {
        this.$data.supplierDefaultTaxId = null
        if(response.data.supplierTaxRate) {
          this.$data.supplierDefaultTaxId = response.data.supplierTaxRate.id
          this.$data.taxName = response.data.supplierTaxRate.displayName
        }
      })

      this.$apiService.pricingService.getSupplierBulkPricingList(this.$props.contactId)
      .then(response => {
        this.supplierBulkPricingData = response.data
      })
    },
    addItemClicked() {
      var bulkItem = {
        loading: false,
        stockType: InventoryStockTypes.glass,
       stockId: null,
        stock: {},
        description: '',
        glassHeight: 0,
        glassWidth: 0,
       glassSizeId: null,
        glassSize: {},
        blockSize: null,
        blockQuantity: 0,
        stockContainerCode: '',
        unitPrice: 0,
        priceError: '',
       taxRateId: this.$data.supplierDefaultTaxId,
        glassSizeList: [],
        glassSizeOptions: []
      }
      this.itemList.push(bulkItem)

      this.taxRateUpdated(bulkItem, bulkItem.taxRateId)

      this.itemListUpdated()
    },
    removeItem(index) {
      this.itemList.splice(index, 1)
      this.itemListUpdated()
    },
    itemListUpdated() {
      this.$emit('update', this.itemList)

      // Update totals
      var totals = {
        subTotal: this.calculateSubTotalPrice(),
        tax: this.calculateTaxPrice(),
        total: this.calculateTotalPrice()
      }
      this.$emit('updateTotals', totals)

      // Update summary details
      var summaryInfo = {
        containerCode: this.calculateContainerInfo(),
        totalBlockQuantity: this.totalBlockQuantity(),
        totalContainerBlockCapacity: this.totalContainerBlockCapacity(),
        containerOverCapacity: this.containerOverCapacity(),
        containsMixedSizes: this.containsMixedSizes(),
        containsMixedContainers: this.containsMixedContainers()
      }
      this.$emit('updateSummary', summaryInfo)
    },
    setItemStockId(item, stockId, updateList = true)
    {
      return new Promise((resolve, reject) => {
        item.stockId = stockId
        if(item.stockId == null) {
          item.loading = false
          if(updateList)
            this.itemListUpdated()
          return resolve()
        }

        item.loading = true

        // Get supplier alias
        if(this.supplierBulkPricingData.stockBulkPriceList) {
          let stockSupplierInfo = this.supplierBulkPricingData.stockBulkPriceList.find(stock => stock.id == item.stockId)
          item.description = ''
          if(stockSupplierInfo)
            item.description = stockSupplierInfo.alias
        }

        this.$apiService.inventoryService.getStock(item.stockId)
        .then(response => {
          let stock = response.data
          item.stock = {
            id: stock.id,
            code: stock.code,
            name: stock.name,
            thickness: stock.thickness || 0
          }

          // Get the glass sizing list for the stock
          item.glassSizeList = response.data.glassSizeList
          item.glassSizeOptions = item.glassSizeList.map(glassSize => {
            return { value: glassSize.id, name: `${glassSize.height} x ${glassSize.width}` }
          })
          if(item.glassSizeOptions.length > 0) {
            var glassSizeId = item.glassSizeId

            this.glassSizeUpdated(item, glassSizeId)
          }
        })
        .finally(() => {
          item.loading = false
          if(updateList)
            this.itemListUpdated()

          return resolve()
        })
      })
    },
    glassSizeUpdated(item, glassSizeId, customOptions = {}) {
      item.glassSizeId = glassSizeId

      // Set details
      item.stockContainerCode = ''
      item.blockSize = null
      item.unitPrice = 0
      item.priceError = ''
      if(customOptions.isCustomStockLinkedGlassSize !== undefined)
        item.isCustomStockLinkedGlassSize = !!customOptions.isCustomStockLinkedGlassSize

      var glassSize = item.glassSizeList.find(glassSize => glassSize.id == glassSizeId)
      if(glassSize) {
        item.glassSize = {
          id: glassSize.id,
          blockSize: glassSize.blockSize,
          stockContainer: {}
        }

        if(glassSize.stockContainer) {
          item.stockContainerCode = glassSize.stockContainer.code
          item.glassSize.stockContainer = {
            id: glassSize.stockContainer.id,
            code: glassSize.stockContainer.code,
            name: glassSize.stockContainer.name
          }
        }
        item.blockSize = parseInt(glassSize.blockSize)
        item.glassHeight = parseInt(glassSize.height)
        item.glassWidth = parseInt(glassSize.width)

        if(customOptions.isCustomStockLinkedGlassSize !== undefined && item.isCustomStockLinkedGlassSize) {
          item.glassHeight = parseInt(customOptions.glassHeight)
          item.glassWidth = parseInt(customOptions.glassWidth)
        }
        
        var areaMM = this.parseDecimal(item.glassHeight) * this.parseDecimal(item.glassWidth)
        item.units = Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(areaMM))

        item.priceError = 'Supplier has not price data for this stock / size'

        // Calculate pricing for supplier / stock / container combination
        if(glassSize.stockContainer) {
          var stockPrice = this.supplierBulkPricingData.stockBulkPriceList.find(stock => stock.id == item.stockId)
          if(stockPrice) {
            var priceInfo = stockPrice.prices.find(containerPrice => containerPrice.stockContainerId == glassSize.stockContainer.id)
            if(priceInfo) {
              item.unitPrice = priceInfo.price
              item.priceError = ''
            }
          }
        }
      }
    },

    taxRateUpdated(item, taxRateId) {
      // Set actual tax rate for the item based on the list
      let taxInfo = this.$data.taxRateList.find(taxRate => taxRate.id == taxRateId)

      item.taxRate = taxInfo
      item.taxRatePercent = taxInfo ? taxInfo.rate : 0
    },

    containsMixedSizes() {
      let list = this.itemList.filter(item => item.stockId && !item.loading)
      if(list.length == 0)
        return false
      return list.filter(item => item.glassHeight != list[0].glassHeight || item.glassWidth != list[0].glassWidth).length > 0
    },

    containsMixedContainers() {
      let list = this.itemList.filter(item => item.stockId && !item.loading)
      if(list.length == 0)
        return false
      return list.filter(item => item.stockContainerCode != list[0].stockContainerCode).length > 0
    },

    calculateContainerInfo() {
      if(this.itemList.length == 0)
        return '-'

      return this.itemList[0].stockContainerCode
    },
    totalBlockQuantity() {
      return this.itemList.filter(item => item.stockId).reduce((count, item) => count + parseInt(item.blockQuantity), 0)
    },
    totalContainerBlockCapacity() {
      if(this.itemList.length == 0 || this.containerList.length == 0)
        return 0
      
      let container = this.containerList.find(container => container.code == this.itemList[0].stockContainerCode)
      if(container)
        return parseInt(container.blockQuantity)
      return 0
    },
    containerOverCapacity() {
      return this.totalBlockQuantity() > this.totalContainerBlockCapacity()
    },

    uomItemDisplayUnits(item) {
      var stock = this.stockList.find(stock => stock.id == item.stockId)
      if(stock)
        return Utils.getLookupName(UOMTypeUnitsLookup, stock.uomType)
      return ''
    },

    calculateItemTotalUnits(item) {
      if(this.editing) {
        if(!item.glassSizeList)
          return 0
        var glassSize = item.glassSizeList.find(glassSize => glassSize.id == item.glassSizeId)
        if(!glassSize)
          return 0

        let glassHeight = glassSize.height
        let glassWidth = glassSize.width
        if(item.isCustomStockLinkedGlassSize) {
          glassHeight = item.glassHeight
          glassWidth = item.glassWidth
        }

        // Calculate total mm^2 and return as m^2
        var areaMM = (this.parseDecimal(glassHeight) * this.parseDecimal(glassWidth))
        let units = Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(areaMM))
        return Utils.roundQuantityValue(units * parseInt(item.blockSize) * parseInt(item.blockQuantity))
      } else {

        return Utils.roundQuantityValue(item.units * parseInt(item.blockSize) * parseInt(item.blockQuantity))
      }
    },

    calculateItemLinePrice(item) {
      return this.calculateItemTotalUnits(item) * Utils.roundCurrency(item.unitPrice)
    },
    calculateItemWeight(item) {
      return Utils.roundNumber(Utils.calculateGlassWeightForArea(this.calculateItemTotalUnits(item), item.stock.thickness))
    },
    calculateTotalWeight() {
       var totalWeight = 0
      if(this.itemList != null) {
        this.itemList.forEach(item => {
          totalWeight += this.calculateItemWeight(item)
        })
      }
      return totalWeight
    },

    calculateSubTotalPrice() {
      var totalPrice = 0
      if(this.itemList != null) {
        this.itemList.forEach(item => {
          totalPrice += Utils.roundCurrency(this.calculateItemLinePrice(item))
        })
      }
      return totalPrice
    },
    calculateTaxPrice() {
      var totalTaxPrice = currency(0, { precision: 4})
      if(this.itemList != null) {
        this.itemList.forEach(item => {

          let itemPrice = Utils.roundCurrency(this.calculateItemLinePrice(item))
          if(item.taxRatePercent)
            totalTaxPrice = totalTaxPrice.add(currency(item.taxRatePercent, { precision: 4}).multiply(0.01).multiply(itemPrice))
        })
      }
      return totalTaxPrice.value
    },
    calculateTotalPrice() {
      return this.calculateSubTotalPrice() + this.calculateTaxPrice()
    },
    priceError() {
      return this.itemList.filter(item => item.priceError).length > 0
    },


    // Add an order bulk item directly via code
    // Used when loading purchase orders or creating new purchase orders from an existing order
    // Returns a promise resolved on completion
    addBulkOrderItem(sourceItem) {
      return new Promise(async (resolve, reject) => {

        var newEntryItem = {
          loading: true,
          stockType: sourceItem.stockType,   // Should always be glass for bulk
          stockId: null,
          description: '',
          glassHeight: 0,
          glassWidth: 0,
          glassSizeId: null,
          isCustomStockLinkedGlassSize: false,
          blockSize: null,
          blockQuantity: 0,
          stockContainerCode: '',
          unitPrice: 0,
          priceError: '',
          taxRateId: this.$data.supplierDefaultTaxId,
          glassSizeList: [],
          glassSizeOptions: []
        }

        this.$nextTick(async () => {

          // Set stock & then request stock details
          await this.setItemStockId(newEntryItem, sourceItem.stockId, false)        

          // Set glass size info
          this.glassSizeUpdated(newEntryItem, sourceItem.glassSizeId, {
            isCustomStockLinkedGlassSize: sourceItem.isCustomStockLinkedGlassSize,
            glassHeight: sourceItem.glassHeight,
            glassWidth: sourceItem.glassWidth
          })

          // Fill in the details
          newEntryItem.description = sourceItem.description || ''
          newEntryItem.blockQuantity = sourceItem.blockQuantity
          newEntryItem.unitPrice = sourceItem.unitPrice ? sourceItem.unitPrice : newEntryItem.unitPrice
          newEntryItem.taxRateId = sourceItem.taxRateId ? sourceItem.taxRateId : newEntryItem.taxRateId

          this.taxRateUpdated(newEntryItem, newEntryItem.taxRateId)
        
          this.itemList.push(newEntryItem)
          this.itemListUpdated()
        })

        return resolve()
      })
    },

    selectGlassSize(item) {
      this.displayModal(OrderSelectGlassSizeDialog, { 
        stockId: parseInt(item.stockId),
        glassSizeId: parseInt(item.glassSizeId || 0),
        isCustomGlassSizeAvailable: true,
        isCustomStockLinkedGlassSize: item.isCustomStockLinkedGlassSize,
        customGlassWidth: parseInt(item.glassWidth || 0),
        customGlassHeight: parseInt(item.glassHeight || 0),
      })
      .then(glassSizeInfo => {
        if(glassSizeInfo) {

          this.glassSizeUpdated(item, glassSizeInfo.id, {
            isCustomStockLinkedGlassSize: glassSizeInfo.isCustomStockLinkedGlassSize,
            glassWidth: glassSizeInfo.width,
            glassHeight: glassSizeInfo.height
          })
          this.itemListUpdated()
        }
      })
    }
  }
}
</script>